import { makeAutoObservable } from 'mobx';
import { AmountToPayStore } from 'account/amount-to-pay/amount-to-pay.store';
import { closeDialog, showDialog } from 'components/dialog-wrapper';
import { Quote } from 'core/models/account/amount-to-pay.dto';
import { GlobalUIStore } from 'core/stores/global-ui.store';
import { ProfileStore } from 'profile/profile.store';
import { DialogId } from './index';

export enum SetupStep {
  none,
  intro,
  quoteSummary,
  quoteSummaryBreakdown
}

export type RecurringPaymentCardDetails = {
  status: string;
  token?: string;
  encryptedcvv?: string;
}

type ErrorDialogProps = {
  title?: string;
  text?: string;
  onClick?: () => void;
  buttonText?: undefined | 'ok' | 'done' | 'tryAgain';
}

const mobileMaxWidth = 600;

class RecurringPaymentComponentStore {
  quote?: Quote;
  setupStep = SetupStep.none;
  showPaymentIframe = false;
  cardDetails?: RecurringPaymentCardDetails;
  isEnabled: boolean | undefined = undefined;
  showErrorMessage: boolean = false;
  errorDetails?: ErrorDialogProps = undefined;
  amountToPayStore = new AmountToPayStore();
  profileStore = new ProfileStore();
  dialogToDisplay?: string;
  isRedirecting = false;

  constructor() {
    makeAutoObservable(this);
  }

  changeIsEnabled(value: boolean) {
    this.isEnabled = value;
  }

  changeIsRedirecting(value: boolean) {
    this.isRedirecting = value;

    value
      ? this.showDialog(DialogId.redirecting)
      : this.closeDialog(DialogId.redirecting);
  }

  changeQuote(quote?: Quote) {
    if (quote) {
      //quote from amount to pay doesn't have this property
      quote.payableAmount = parseInt(quote.total?.value ?? "0");
    }

    this.quote = quote;
  }

  get isMobile() {
    return GlobalUIStore.GetInstance.screenWidth <= mobileMaxWidth;
  }

  changeSetupStep(value: SetupStep) {
    this.setupStep = value;
  }

  changeShowPaymentIframe(value: boolean) {
    this.showPaymentIframe = value;
  }

  changeCardDetails(details: RecurringPaymentCardDetails) {
    this.cardDetails = details;
  }

  showError(message?: string, title = 'Error', buttonText: 'ok' | 'done' | 'tryAgain' = 'ok', onClick?: () => void) {
    this.errorDetails = {
      title,
      text: message,
      buttonText,
      onClick: () => !!onClick ? onClick() : this.closeDialog(DialogId.error)
    };

    this.showErrorMessage = true
    this.showDialog(DialogId.error);
  }

  showDialog(id: string) {
    this.dialogToDisplay = id;
    showDialog(id);
  }

  closeDialog(id: string) {
    closeDialog(id);

    //potentially a new dialog might be opened before closing the current one.
    this.dialogToDisplay = this.dialogToDisplay === id
      ? undefined
      : this.dialogToDisplay;
  }

  clearError() {
    this.closeDialog(DialogId.error);
    this.errorDetails = undefined;
    this.showErrorMessage = false;
  }

  get redirecting() {
    const context = this;
    return {
      open: () => context.changeIsRedirecting(true),
      close: () => context.changeIsRedirecting(false),
    };
  }
}

export default RecurringPaymentComponentStore;